import React from 'react';

import { callbackHooks } from '@nxte-nl/callback-hooks';
import { prerenderHooks, PageState } from '@nxte-nl/prerender-hooks';

import {
  AppDataStoreData,
  BasketMessages,
  CustomerSupportMessages,
  GlobalMessages,
  Messages,
  ProductsListMessages,
} from '../../types/Messages';
import { Apis, PrerenderingOptions } from '../../types/Prerendering';
import { messageHooks } from '../messageHooks/messageHooks';

type UseSetLoadingToMessageParams = {
  loading: boolean;
  message: keyof Messages;
};

export const pageStateHooks = {
  usePageReady(options: PrerenderingOptions = {}) {
    const entity = messageHooks.useSubjectState(GlobalMessages.PageEntity);
    const storeData = messageHooks.useSubjectState(AppDataStoreData.StoreData);
    const blocksQueue = messageHooks.useSubjectState(ProductsListMessages.BlocksQueue);
    const products = messageHooks.useSubjectState(ProductsListMessages.Products);
    const cart = messageHooks.useSubjectState(BasketMessages.Cart);
    const csIframe = messageHooks.useSubjectState(CustomerSupportMessages.IframeLoaded);

    const pageState: PageState<Apis> = React.useMemo(() => ({
      entity,
      storeData,
      blocksQueue,
      products,
      cart,
      csIframe,
    }), [entity, storeData, blocksQueue, products, cart, csIframe]);

    return prerenderHooks.useCumulatedState(options, pageState);
  },

  useSetLoadingToMessage({
    loading,
    message,
  }: UseSetLoadingToMessageParams) {
    const setLoading = messageHooks.useNextWithValue(
      message,
      loading,
    );

    callbackHooks.useEffectCallback(setLoading);
  },

  usePageContentLoading(loading: boolean) {
    pageStateHooks.useSetLoadingToMessage({
      loading,
      message: GlobalMessages.PageContentLoading,
    });
  },

  useGlobalMutationLoading(loading: boolean) {
    pageStateHooks.useSetLoadingToMessage({
      loading,
      message: GlobalMessages.MutationInProgress,
    });
  },

  useGetEntityStates() {
    const pageEntityLoading = messageHooks.useSubjectState(GlobalMessages.PageEntityLoading);
    const [entityLoaded, setEntityLoaded] = React.useState<boolean>(false);
    const previousEntityLoading = pageStateHooks.usePrevioustDataLoadedState(pageEntityLoading);
    return {
      pageEntityLoading,
      entityLoaded,
      setEntityLoaded,
      previousEntityLoading,
    };
  },
  useEntityLoaded() {
    const {
      pageEntityLoading,
      entityLoaded,
      setEntityLoaded,
      previousEntityLoading,
    } = pageStateHooks.useGetEntityStates();

    React.useEffect(() => {
      if (previousEntityLoading && !pageEntityLoading) {
        setEntityLoaded(true);
      }
    }, [pageEntityLoading, previousEntityLoading, setEntityLoaded]);
    return { entityLoaded, pageEntityLoading };
  },
  usePrevioustDataLoadedState(flag: boolean) {
    const ref = React.useRef<boolean>(flag);
    React.useEffect(() => {
      ref.current = flag;
    }, [flag]);
    return ref.current;
  },
  useGetStatesForDataLoading() {
    const [isDataLoaded, setIsDataLoaded] = React.useState<boolean>(false);
    return {
      isDataLoaded,
      setIsDataLoaded,
    };
  },
  useIsLoadedData(loading: boolean) {
    const {
      isDataLoaded,
      setIsDataLoaded,
    } = pageStateHooks.useGetStatesForDataLoading();
    const previousLoading = pageStateHooks.usePrevioustDataLoadedState(loading);
    React.useEffect(() => {
      if (previousLoading && !loading) {
        setIsDataLoaded(true);
      }
    }, [loading, previousLoading, setIsDataLoaded]);
    return { isDataLoaded };
  },
};
